import React, { useState, useEffect } from 'react';
import { database } from '../firebaseConfig';
import { ref, onValue, query, orderByChild, limitToLast, update, remove } from 'firebase/database';
import { getFirestore, collection, query as firestoreQuery, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import './EmailDraftsPreview.css';

const EmailDraftsPreview = ({ userId, onOpenDraftInEmailPopup, onOpenDraftBulkMode, onNavigateToSettings }) => {
  const [drafts, setDrafts] = useState([]);
  const [safeGuardDrafts, setSafeGuardDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingDrafts, setSendingDrafts] = useState(new Set());
  const [hasAgents, setHasAgents] = useState(false);
  
  const firestoreDB = getFirestore();

  useEffect(() => {
    if (!userId) return;
    const agentsRef = ref(database, `/World/${userId}/settings/agents`);
    const unsubscribe = onValue(agentsRef, (snapshot) => {
      setHasAgents(snapshot.exists() && snapshot.hasChildren());
    });
    return () => unsubscribe();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    console.log('📧 [DRAFTS] Loading email drafts for user:', userId);
    
    const draftsRef = query(
      ref(database, `/World/${userId}/emailDrafts`),
      orderByChild('timestamp'),
      limitToLast(20) // Erhöht auf 20 für horizontales Layout
    );

    const unsubscribe = onValue(draftsRef, (snapshot) => {
      if (snapshot.exists()) {
        const draftsData = [];
        snapshot.forEach((childSnapshot) => {
          const draft = {
            id: childSnapshot.key,
            ...childSnapshot.val()
          };
          // Nur Drafts mit Status "not sent" anzeigen
          if (draft.status === 'not sent') {
            draftsData.push(draft);
          }
        });
        
        // Sortiere nach Timestamp (neueste zuerst)
        draftsData.sort((a, b) => b.timestamp - a.timestamp);
        setDrafts(draftsData);
        console.log(`📧 [DRAFTS] ${draftsData.length} unsent drafts loaded successfully`);
        
        // Log Draft-Details für Debugging
        draftsData.forEach(draft => {
          console.log(`📧 [DRAFT] ID: ${draft.id}, Agent: ${draft.agentName}, Status: ${draft.status}, Created: ${draft.createdAt}`);
        });
      } else {
        setDrafts([]);
        console.log('📧 [DRAFTS] No drafts found in database');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // Lade SafeGuard Drafts aus Firestore
  useEffect(() => {
    if (!userId) return;

    console.log('🛡️ [SAFEGUARD-DRAFTS] Loading SafeGuard drafts for user:', userId);
    
    const safeGuardDraftsRef = collection(firestoreDB, 'world_drafts', userId, 'emails');
    const safeGuardQuery = firestoreQuery(
      safeGuardDraftsRef,
      where('status', '==', 'draft'),
      where('reason', 'in', ['safeguard_rejection', 'scorer_rejection', 'generation_error', 'crm_blocked', 'reply_received', 'email_invalid_final']),
      orderBy('timestamp', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(safeGuardQuery, (snapshot) => {
      const safeGuardDraftsData = [];
      snapshot.forEach((doc) => {
        const draftData = doc.data();
        safeGuardDraftsData.push({
          id: doc.id,
          ...draftData,
          type: 'safeguard',
          createdAt: draftData.date || new Date().toISOString(),
          timestamp: draftData.timestamp?.toMillis?.() || Date.now()
        });
      });
      
      setSafeGuardDrafts(safeGuardDraftsData);
      console.log(`🛡️ [SAFEGUARD-DRAFTS] ${safeGuardDraftsData.length} SafeGuard drafts loaded successfully`);
      
      // Log SafeGuard Draft-Details für Debugging
      safeGuardDraftsData.forEach(draft => {
        console.log(`🛡️ [SAFEGUARD-DRAFT] ID: ${draft.id}, Rating: ${draft.safeGuardRating}, Decision: ${draft.safeGuardDecision}, Subject: ${draft.subject}`);
      });
    }, (error) => {
      console.error('🛡️ [SAFEGUARD-DRAFTS] Error loading SafeGuard drafts:', error);
    });

    return () => unsubscribe();
  }, [userId, firestoreDB]);

  // Formatiere Datum
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Kürze Text
  const truncateText = (text, maxLength = 120) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Extrahiere Absender-Name aus E-Mail
  const extractSenderName = (fromEmail) => {
    if (!fromEmail) return 'Unknown';
    
    // Extrahiere Name aus "Name <email@domain.com>" Format
    const nameMatch = fromEmail.match(/^([^<]+)</);
    if (nameMatch) {
      return nameMatch[1].trim();
    }
    
    // Falls nur E-Mail-Adresse, extrahiere den Teil vor @
    const emailMatch = fromEmail.match(/([^@]+)@/);
    return emailMatch ? emailMatch[1] : fromEmail;
  };

  // Kombiniere alle Drafts
  const allDrafts = React.useMemo(() => {
    const combined = [
      ...drafts.map(d => ({ ...d, type: 'agent' })),
      ...safeGuardDrafts
    ];
    
    // Sortiere nach Timestamp (neueste zuerst)
    return combined.sort((a, b) => b.timestamp - a.timestamp);
  }, [drafts, safeGuardDrafts]);

  // Handle Draft Selection - unterscheidet zwischen Agent und SafeGuard Drafts
  const handleDraftSelection = (draft) => {
    if (draft.type === 'safeguard') {
      // SafeGuard Draft: Öffne direkt im EmailAiPopup für Bearbeitung
      if (onOpenDraftInEmailPopup) {
        const emailReplyData = {
          // Basis E-Mail-Daten für EmailAiPopup
          from: draft.from,
          to: draft.to,
          subject: `Re: ${draft.subject}`,
          date: draft.createdAt,
          content: draft.content,
          bodyText: draft.content,
          body: draft.content,
          
          // SafeGuard-spezifische Daten
          safeGuard: {
            id: draft.id,
            rating: draft.safeGuardRating,
            info: draft.safeGuardInfo,
            decision: draft.safeGuardDecision,
            issues: draft.safeGuardIssues || [],
            improvements: draft.safeGuardImprovements || [],
            minimumRating: draft.minimumRating,
            type: 'safeguard_draft'
          },
          
          // Metadaten
          businessName: draft.businessName,
          leadId: draft.leadId,
          emailType: draft.emailType
        };
        
        onOpenDraftInEmailPopup(emailReplyData);
      }
    } else {
      // Agent Draft: Öffne immer den Multi-Reply-Modus
      if (onOpenDraftBulkMode && allDrafts.length > 0) {
        const selectedIndex = allDrafts.findIndex(d => d.id === draft.id);
        // Filtere nur Agent-Drafts für Bulk Mode
        const agentDrafts = allDrafts.filter(d => d.type === 'agent');
        const agentIndex = agentDrafts.findIndex(d => d.id === draft.id);
        onOpenDraftBulkMode(agentDrafts, agentIndex);
      }
    }
  };

  // Funktion zum optimistischen Entfernen eines Drafts aus der lokalen Liste
  const markDraftAsSending = (draftId) => {
    setSendingDrafts(prev => new Set([...prev, draftId]));
    
    // Entferne den Draft sofort aus der lokalen Liste
    setDrafts(prevDrafts => prevDrafts.filter(draft => draft.id !== draftId));
  };

  // Exponiere die markDraftAsSending Funktion über eine Referenz
  useEffect(() => {
    if (window.emailDraftsPreview) {
      window.emailDraftsPreview.markDraftAsSending = markDraftAsSending;
    } else {
      window.emailDraftsPreview = { markDraftAsSending };
    }
    
    return () => {
      if (window.emailDraftsPreview) {
        delete window.emailDraftsPreview.markDraftAsSending;
      }
    };
  }, []);

  // Neue Funktion zum Löschen eines Drafts
  const handleDelete = async (e, draftId) => {
    e.stopPropagation(); // Verhindert das Öffnen des Drafts beim Löschen
    
    try {
      console.log(`📧 [DRAFTS] Deleting draft ${draftId}`);
      const draftRef = ref(database, `/World/${userId}/emailDrafts/${draftId}`);
      await remove(draftRef);
      console.log(`📧 [DRAFTS] Successfully deleted draft ${draftId}`);
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  if (loading) {
    return (
      <div className="emailDraftsPreview">
        <div className="draftsHeader">
          <div className="draftsHeaderLeft">
            <div className="draftsHeaderIcon">
              <div className="iconContainer">
                <img src="/img/mailbox.svg" alt="AI Replies" />
              </div>
            </div>
            <h4>Inbox Agent</h4>
          </div>
        </div>
        <div className="draftsContainer">
          <div className="draftsList">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="draftItemWrapper draftSkeletonWrapper" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="draftItem draftSkeletonItem">
                  <div className="draftHeader">
                    <div className="draftSkeletonLine draftSkeletonName shimmer" />
                    <div className="draftSkeletonLine draftSkeletonDate shimmer" />
                  </div>
                  <div className="draftSkeletonLine draftSkeletonSubject shimmer" />
                  <div className="draftSkeletonBody">
                    <div className="draftSkeletonLine shimmer" style={{ width: '100%' }} />
                    <div className="draftSkeletonLine shimmer" style={{ width: '85%' }} />
                    <div className="draftSkeletonLine shimmer" style={{ width: '60%' }} />
                  </div>
                  <div className="draftSkeletonMeta">
                    <div className="draftSkeletonLine draftSkeletonAgent shimmer" />
                    <div className="draftSkeletonLine draftSkeletonStatus shimmer" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (drafts.length === 0) {
    if (hasAgents) {
      return (
        <div className="emailDraftsPreview noDraftsGhost">
          <div className="draftsHeader">
            <div className="draftsHeaderLeft">
              <div className="draftsHeaderIcon">
                <div className="iconContainer">
                  <img src="/img/mailbox.svg" alt="AI Replies" />
              </div>
            </div>
            <h4>Inbox Agent</h4>
          </div>
        </div>
        <div className="draftsContainer">
          <div className="draftsList">
            <div className="draftItemWrapper draftWaitingWrapper">
                <div className="draftItem draftWaitingItem">
                  <div className="draftWaitingIcon">
                    <img src="/img/mailbox.svg" alt="" />
                    <div className="draftWaitingPulse" />
                  </div>
                  <div className="draftWaitingText">
                    <h5>Waiting for your first reply</h5>
                    <p>Your AI agents are active. As soon as a matching email arrives, draft replies will appear here.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="emailDraftsPreview noDraftsGhost">
        <div className="draftsHeader">
          <div className="draftsHeaderLeft">
            <div className="draftsHeaderIcon">
              <div className="iconContainer">
                <img src="/img/mailbox.svg" alt="AI Replies" />
              </div>
            </div>
            <h4>Inbox Agent</h4>
          </div>
        </div>
        <div className="draftsContainer">
          <div className="draftsList">
            <div className="draftItemWrapper draftGhostWrapper" onClick={onNavigateToSettings}>
              <div className="draftItem draftGhostItem">
                <div className="draftGhostTop">
                  <div className="draftGhostAvatar">
                    <img src="/img/brain.svg" alt="" />
                  </div>
                  <div className="draftGhostMeta">
                    <div className="draftGhostLine" style={{ width: '55%', height: '10px' }} />
                    <div className="draftGhostLine" style={{ width: '35px', height: '8px', opacity: 0.3 }} />
                  </div>
                </div>
                <div className="draftGhostLine" style={{ width: '70%', height: '9px' }} />
                <div className="draftGhostBody">
                  <div className="draftGhostLine" style={{ width: '100%' }} />
                  <div className="draftGhostLine" style={{ width: '85%' }} />
                  <div className="draftGhostLine" style={{ width: '45%' }} />
                </div>
                <div className="draftGhostCta">
                  <span className="draftGhostCtaDot" />
                  <span>Configure Inbox Agent</span>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="emailDraftsPreview">
      <div className="draftsHeader">
        <div className="draftsHeaderLeft">
          <div className="draftsHeaderIcon">
            <div className="iconContainer">
              <img src="/img/mailbox.svg" alt="AI Replies" />
            </div>
          </div>
          <h4>{drafts.length} Inbox Agent</h4>
          {drafts.length > 1 && (
            <button 
              className="draftsBulkEditButton"
              onClick={() => onOpenDraftBulkMode(drafts, 0)}
              disabled={drafts.length === 0}
            >
              Multi Reply
              <div className="buttonIcon">
                <img src="/img/paperplane.svg" alt="Multi Reply" />
              </div>
            </button>
          )}
        </div>
      </div>
      
      <div className="draftsContainer">
        <div className="draftsList">
          {drafts.map((draft) => (
            <div 
              key={draft.id} 
              className="draftItemWrapper"
            >
              <div 
                className="draftDeleteIcon"
                onClick={(e) => handleDelete(e, draft.id)}
                title="Delete draft"
              >
                <img src="/img/delete.svg" alt="Delete" />
              </div>
              <div 
                className="draftItem"
                onClick={() => handleDraftSelection(draft)}
              >
                <div className="draftHeader">
                  <div className="draftSender">
                    {extractSenderName(draft.originalEmail?.from)}
                  </div>
                  <div className="draftDate">
                    {formatDate(draft.createdAt)}
                  </div>
                </div>
                
                <div className="draftSubject">
                  Re: {draft.originalEmail?.subject || 'No Subject'}
                </div>
                
                <div className="draftPreview">
                  {truncateText(draft.draftContent, 240)}
                </div>
                
                <div className="draftMeta">
                  <div className="draftAgent">
                    <span className="agentIcon">
                      <img style={{width: '10px', height: '10px'}} src="/img/brain.svg" alt="AI Replies" />
                    </span>
                    {draft.agentName} (Case {draft.agentCaseNumber})
                  </div>
                  <div className="draftStatus">
                    <span className="statusDot"></span>
                    Ready to Send
                  </div>
                  {draft.originalEmail?.emailTopic && (
                    <div className="draftTopic">
                      <span className="topicIcon">
                        <img style={{width: '8px', height: '8px'}} src="/img/star.svg" alt="AI Replies" />
                      </span>
                      {draft.originalEmail.emailTopic}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmailDraftsPreview; 